import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { useTar, TranslationKey } from '@/modules'
import { apis } from '../../modules/apiClients'
import { UserService } from '@duck/api-definitions'
import { RelativeDateTime } from '../datetime/RelativeDateTime'
import {
    MultiPick,
    Button,
    Checkbox,
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    Textarea,
} from '@duck/ui'
import { UserDataInput } from '../DataInputs/UserDataInput'
import { InvitationDialog } from '../AdminInvitations/InvitationDialog'
import { useAdminWaitingList } from '@/hooks/useAdminWaitingList'
import { useLoadMoreOnScroll } from '@/hooks/useLoadMoreOnScroll'

interface Column {
    key: keyof UserService.WaitingListEntry | 'actions' | 'select'
    label: TranslationKey
    sortable: boolean
    visible: boolean
    renderer?: (subscription: UserService.WaitingListEntry) => React.ReactNode
}

interface BulkInviteFormData {
    accessGroups: string[]
    validFor: number
    customBody: string
    inviterId: string
    inviterName: string
}

// Icon mapping for different sources
const sourceIconMap: Record<string, React.ReactNode> = {
    [UserService.HeardOfUsSource.Instagram]: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
        </svg>
    ),
    [UserService.HeardOfUsSource.Facebook]: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
    ),
    [UserService.HeardOfUsSource.Linkedin]: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
        </svg>
    ),
    [UserService.HeardOfUsSource.Reddit]: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 3.243.913.759 0 2.401-.071 3.243-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.808.73-1.124 0-2.261-.197-2.808-.73a.326.326 0 0 0-.232-.095z" />
        </svg>
    ),
    [UserService.HeardOfUsSource.X]: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
    ),
    [UserService.HeardOfUsSource.ChatGpt]: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M11.586 2.836l-9.024 3.852c-.377.161-.596.558-.562.971.034.413.318.766.705.905l7.338 2.778v8.658c0 .414.448.793.955.676.507-.117.885-.569.885-1.057V11.15c0-.11-.015-.216-.044-.32l-1.507-5.062L12.4 5.71c.377-.16.596-.557.563-.97-.034-.413-.318-.766-.705-.905z" />
        </svg>
    ),
    [UserService.HeardOfUsSource.Gemini]: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2L2 7l10 5 10-5-10-5z" />
            <path d="M2 17l10 5 10-5" />
            <path d="M2 12l10 5 10-5" />
        </svg>
    ),
    [UserService.HeardOfUsSource.Claude]: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
        </svg>
    ),
    [UserService.HeardOfUsSource.Google]: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="currentColor" />
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="currentColor" />
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="currentColor" />
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="currentColor" />
        </svg>
    ),
    [UserService.HeardOfUsSource.Bing]: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M5.71 3l12.58 10-.19.22c-.19.22-.41.38-.67.52l-.16.09-3.52 1.97c-.11.06-.24.06-.35 0l-3.52-1.97c-.36-.2-.66-.52-.86-.91l-.01-.03L3.76 9.14l-.02-.01c-.06-.12-.06-.25 0-.37l.01-.01L5.71 3z" />
        </svg>
    ),
    [UserService.HeardOfUsSource.Yandex]: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h2.08c.1 1.05.82 1.87 2 2v-4H9.59V8.91h4.56c.68 0 1.14.47 1.41.81v3.9c1.12-.03 1.94-.88 2-2h2.02c-.09 1.96-1.57 3.05-3.17 3.28z" />
        </svg>
    ),
    [UserService.HeardOfUsSource.Email]: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
        </svg>
    ),
    [UserService.HeardOfUsSource.WordOfMouth]: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z" />
        </svg>
    ),
}

const defaultColumns: Column[] = [
    { key: 'select', label: 'common.select', sortable: false, visible: true },
    { key: 'email', label: 'common.email', sortable: true, visible: true },
    { key: 'firstName', label: 'common.firstName', sortable: true, visible: true },
    { key: 'lastName', label: 'common.lastName', sortable: true, visible: true },
    {
        key: 'heardOfUs', label: 'common.heardOfUs', sortable: true, visible: true,
        renderer: (subscription) => {
            if (subscription.heardOfUs === UserService.HeardOfUsSource.Other) {
                return subscription.heardOfUsOther || subscription.heardOfUs
            }
            const icon = subscription.heardOfUs ? sourceIconMap[subscription.heardOfUs] : null
            if (icon) {
                return (<div className="flex items-center gap-2">{icon}<span>{subscription.heardOfUs}</span></div>)
            }
            return subscription.heardOfUs
        },
    },
    {
        key: 'createdAt', label: 'common.subscribedAt', sortable: true, visible: true,
        renderer: (subscription) => (<RelativeDateTime isoDateString={subscription.createdAt} />),
    },
    {
        key: 'updatedAt', label: 'common.lastUpdated', sortable: true, visible: true,
        renderer: (subscription) => (<RelativeDateTime isoDateString={subscription.updatedAt} />),
    },
    { key: 'actions', label: 'common.actions', sortable: false, visible: true },
]

export const AdminWaitingList: React.FC = () => {
    const { t } = useTar()

    // State management
    const [sortField, setSortField] = useState<keyof UserService.WaitingListEntry>('createdAt')
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')
    const [filterText, setFilterText] = useState('')
    const [columns, setColumns] = useState<Column[]>(defaultColumns)

    // Auxiliary API state
    const [allAccessGroups, setAllAccessGroups] = useState<string[]>([])

    // Dialog state
    const [isInvitationDialogOpen, setIsInvitationDialogOpen] = useState(false)
    const [prefilledInvitationData, setPrefilledInvitationData] = useState<Partial<UserService.Invitation> | undefined>(undefined)

    // Selection state for bulk invite
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

    // Bulk invite dialog state
    const [isBulkInviteDialogOpen, setIsBulkInviteDialogOpen] = useState(false)
    const [bulkInviteFormData, setBulkInviteFormData] = useState<BulkInviteFormData>({
        accessGroups: [],
        validFor: 7,
        customBody: '',
        inviterId: '',
        inviterName: '',
    })
    const [isBulkInviting, setIsBulkInviting] = useState(false)
    const [bulkInviteError, setBulkInviteError] = useState<string | null>(null)

    const {
        waitingList: subscriptions,
        hasMore,
        isInitialLoading,
        isFetchingNextPage,
        isError,
        fetchNextPage,
        refetch,
    } = useAdminWaitingList({
        sortField,
        sortDirection,
    })

    // Helper to prune selectedIds based on fresh subscription data
    const pruneSelectedIds = (
        freshSubscriptions: UserService.WaitingListEntry[],
        currentSelectedIds: Set<string>
    ): Set<string> => {
        const validSelectableIds = new Set(
            freshSubscriptions
                .filter(sub => !sub.hasInvitation)
                .map(sub => sub.id)
        )
        const prunedIds = new Set<string>()
        currentSelectedIds.forEach(id => {
            if (validSelectableIds.has(id)) {
                prunedIds.add(id)
            }
        })
        return prunedIds
    }

    // Prune selected IDs when subscriptions change
    useEffect(() => {
        setSelectedIds(prev => pruneSelectedIds(subscriptions, prev))
    }, [subscriptions])

    // Fetch invitations to extract access groups
    const fetchAccessGroups = async () => {
        try {
            const response = await apis.userService.invitationsGet()
            const uniqueAccessGroups = Array.from(
                new Set(response.data.flatMap(invitation => invitation.accessGroups || []))
            ).sort()
            setAllAccessGroups(uniqueAccessGroups)
        } catch {
            console.error('Failed to load access groups list')
        }
    }

    useEffect(() => {
        fetchAccessGroups()
    }, [])

    // Filter subscriptions (client-side text filter)
    const filteredSubscriptions = useMemo(() => {
        let filtered = subscriptions

        if (filterText) {
            filtered = filtered.filter(subscription =>
                subscription.email.toLowerCase().includes(filterText.toLowerCase()) ||
                (subscription.firstName || '').toLowerCase().includes(filterText.toLowerCase()) ||
                (subscription.lastName || '').toLowerCase().includes(filterText.toLowerCase()) ||
                (subscription.heardOfUs || '').toLowerCase().includes(filterText.toLowerCase()) ||
                (subscription.heardOfUsOther || '').toLowerCase().includes(filterText.toLowerCase())
            )
        }

        return filtered
    }, [subscriptions, filterText])

    const handleLoadMorePage = useCallback(() => {
        if (!hasMore) return
        void fetchNextPage()
    }, [hasMore, fetchNextPage])

    const { sentinelRef: loadMoreSentinelRef, sentinelIndex: loadMoreSentinelIndex } = useLoadMoreOnScroll<HTMLTableRowElement>({
        hasMore,
        onLoadMore: handleLoadMorePage,
        isLoading: isFetchingNextPage,
        disabled: isInitialLoading,
        itemCount: filteredSubscriptions.length,
        offsetFromEnd: 10,
    })

    const handleSort = (field: keyof UserService.WaitingListEntry) => {
        if (field === sortField) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')
        } else {
            setSortField(field)
            setSortDirection('asc')
        }
    }

    const toggleColumnVisibility = (columnKey: string) => {
        setColumns(prev => prev.map(col =>
            col.key === columnKey
                ? { ...col, visible: !col.visible }
                : col
        ))
    }

    const handleInviteClick = (subscription: UserService.WaitingListEntry) => {
        setPrefilledInvitationData({
            email: subscription.email,
            firstName: subscription.firstName || '',
            lastName: subscription.lastName || '',
        })
        setIsInvitationDialogOpen(true)
    }

    const handleCloseDialog = () => {
        setIsInvitationDialogOpen(false)
        setPrefilledInvitationData(undefined)
    }

    const handleInvitationSaved = () => {
        fetchAccessGroups()
        void refetch()
    }

    // Selection handlers
    const handleSelectRow = (id: string, checked: boolean) => {
        setSelectedIds(prev => {
            const next = new Set(prev)
            if (checked) {
                next.add(id)
            } else {
                next.delete(id)
            }
            return next
        })
    }

    const handleSelectAll = (checked: boolean) => {
        if (checked) {
            const selectableIds = filteredSubscriptions
                .filter(sub => !sub.hasInvitation)
                .map(sub => sub.id)
            setSelectedIds(new Set(selectableIds))
        } else {
            setSelectedIds(new Set())
        }
    }

    // Get selectable subscriptions (those without invitations)
    const selectableSubscriptions = useMemo(() =>
        filteredSubscriptions.filter(sub => !sub.hasInvitation),
        [filteredSubscriptions]
    )

    const allSelectableSelected = selectableSubscriptions.length > 0 &&
        selectableSubscriptions.every(sub => selectedIds.has(sub.id))

    // Bulk invite handlers
    const handleOpenBulkInviteDialog = () => {
        setBulkInviteFormData({
            accessGroups: [],
            validFor: 7,
            customBody: '',
            inviterId: '',
            inviterName: '',
        })
        setBulkInviteError(null)
        setIsBulkInviteDialogOpen(true)
    }

    const handleCloseBulkInviteDialog = () => {
        if (!isBulkInviting) {
            setIsBulkInviteDialogOpen(false)
        }
    }

    const handleBulkInvite = async () => {
        if (bulkInviteFormData.accessGroups.length === 0) {
            setBulkInviteError(t('invitations.accessGroupsRequired'))
            return
        }

        try {
            setIsBulkInviting(true)
            setBulkInviteError(null)

            const response = await apis.userService.waitingListBulkInvitePost({
                waitingListIds: Array.from(selectedIds),
                accessGroups: bulkInviteFormData.accessGroups,
                validFor: bulkInviteFormData.validFor,
                customBody: bulkInviteFormData.customBody || undefined,
                role: UserService.UserRole.User,
                inviterId: bulkInviteFormData.inviterId || undefined,
                inviterName: bulkInviteFormData.inviterName || undefined,
            })

            const result = response.data

            if (result.failed > 0) {
                setBulkInviteError(t('invitations.bulkInvitePartialSuccess', {
                    successful: result.successful,
                    failed: result.failed
                }))
                const failedWaitingListIds = new Set(
                    result.results
                        .filter(r => !r.success)
                        .map(r => r.waitingListId)
                )
                setSelectedIds(failedWaitingListIds)
                void refetch()
                fetchAccessGroups()
            } else {
                setIsBulkInviteDialogOpen(false)
                setSelectedIds(new Set())
                void refetch()
                fetchAccessGroups()
            }
        } catch (error: unknown) {
            console.error('Failed to send bulk invitations:', error)
            const errorMessage = (error as { response?: { data?: { error?: string } } })?.response?.data?.error
                || t('invitations.bulkInviteFailed')
            setBulkInviteError(errorMessage)
        } finally {
            setIsBulkInviting(false)
        }
    }

    const visibleColumns = columns.filter(col => col.visible)

    const renderCellContent = (subscription: UserService.WaitingListEntry, column: Column) => {
        if (column.key === 'select') {
            return !subscription.hasInvitation ? (
                <Checkbox
                    checked={selectedIds.has(subscription.id)}
                    onCheckedChange={(checked) => handleSelectRow(subscription.id, checked === true)}
                    onClick={(e) => e.stopPropagation()}
                />
            ) : null
        }
        if (column.key === 'actions') {
            return !subscription.hasInvitation ? (
                <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                        e.stopPropagation()
                        handleInviteClick(subscription)
                    }}
                >
                    {t('invitations.invite')}
                </Button>
            ) : <div className="text-sm text-muted-foreground italic">{t('common.invited')}</div>
        }
        const renderer = column.renderer
        return renderer ? renderer(subscription) : subscription[column.key as keyof UserService.WaitingListEntry]
    }

    const renderHeaderContent = (column: Column) => {
        if (column.key === 'select') {
            return (
                <Checkbox
                    checked={allSelectableSelected}
                    onCheckedChange={(checked) => handleSelectAll(checked === true)}
                    disabled={selectableSubscriptions.length === 0}
                />
            )
        }
        if (column.sortable && column.key !== 'actions') {
            return (
                <button
                    onClick={() => handleSort(column.key as keyof UserService.WaitingListEntry)}
                    className="flex items-center gap-1 hover:text-foreground transition-colors"
                >
                    <span>{t(column.label)}</span>
                    {sortField === column.key && (
                        <span className="text-accent">
                            {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                    )}
                </button>
            )
        }
        return t(column.label)
    }

    if (isError) {
        return (
            <div className="border-2 border-border rounded-md p-4 w-fit items-center justify-center absolute top-1/4 start-1/2 -translate-x-1/2 -translate-y-1/2">
                <div className="flex">
                    <div className="text-center">
                        <h3 className="text-sm font-medium">
                            {t('common.waitingListError')}
                        </h3>
                        <div className="mt-4">
                            <button
                                onClick={() => { void refetch() }}
                                className="inline-flex items-center rounded-md bg-accent px-3 py-2 text-sm font-semibold text-accent-foreground shadow-sm hover:bg-accent/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                            >
                                {t('common.retry')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="space-y-6">
            {/* Page Header */}
            <div className="md:flex md:items-center md:justify-between">
                <div className="min-w-0 flex-1">
                    <h2 className="text-2xl font-bold leading-7 text-foreground sm:truncate sm:text-3xl sm:tracking-tight">
                        {t('common.waitingListSubscriptions')}
                    </h2>
                    <p className="mt-1 text-sm text-muted-foreground">
                        {t('common.waitingListSubscriptionsDescription')}
                    </p>
                </div>
                <div className="mt-4 flex md:ms-4 md:mt-0">
                    <button
                        onClick={() => { void refetch() }}
                        className="inline-flex items-center rounded-md bg-accent px-3 py-2 text-sm font-semibold text-accent-foreground shadow-sm hover:bg-accent/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                    >
                        {t('common.refresh')}
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-card shadow rounded-md border border-border">
                <div className="px-4 py-5 sm:p-6">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {/* Search Filter */}
                        <div>
                            <label htmlFor="search" className="ms-1 block text-sm font-medium text-foreground">
                                {t('common.search')}
                            </label>
                            <div className="mt-2">
                                <input
                                    id="search"
                                    type="text"
                                    placeholder={t('common.searchByEmailFirstNameLastName')}
                                    value={filterText}
                                    onChange={(e) => setFilterText(e.target.value)}
                                    className="block w-full ps-2 pe-3 py-2 border border-border rounded-md leading-5 bg-card placeholder-secondary text-foreground focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent"
                                />
                            </div>
                        </div>

                        {/* Column Visibility */}
                        <div className="relative">
                            <label className="ms-1 block text-sm font-medium text-foreground">
                                {t('common.columns')}
                            </label>
                            <div className="mt-2">
                                <MultiPick
                                    options={columns.map(col => ({ value: col.key as string, label: t(col.label) }))}
                                    selected={columns.filter(c => c.visible).map(c => c.key as string)}
                                    onChange={(nextSelected) => {
                                        const selectedSet = new Set(nextSelected)
                                        columns.forEach(col => {
                                            const shouldBeVisible = selectedSet.has(col.key as string)
                                            if (shouldBeVisible !== col.visible) {
                                                toggleColumnVisibility(col.key as string)
                                            }
                                        })
                                    }}
                                    labels={{
                                        itemsLabel: t('common.items'),
                                        placeholder: t('common.selectColumns'),
                                    }}
                                    className="py-[6px]"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="bg-card shadow overflow-hidden rounded-md sm:rounded-md border border-border">
                <div className="px-4 py-5 sm:px-6 flex items-center justify-between">
                    <div>
                        <h3 className="text-lg leading-6 font-medium text-foreground">
                            {t('common.subscriptions')}
                        </h3>
                        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
                            {isInitialLoading ? t('common.loading') : t('common.XRecords', { count: filteredSubscriptions.length })}
                            {selectedIds.size > 0 && ` • ${selectedIds.size} ${t('common.selected')}`}
                        </p>
                    </div>
                    <Button
                        onClick={handleOpenBulkInviteDialog}
                        disabled={selectedIds.size < 1}
                        className="ms-4"
                    >
                        {t('invitations.bulkInvite')} {selectedIds.size > 0 && `(${selectedIds.size})`}
                    </Button>
                </div>

                <div className="overflow-x-auto overflow-y-hidden">
                    <table className="min-w-full divide-y divide-border">
                        <thead className="bg-secondary/5">
                            <tr>
                                {visibleColumns.map((column) => (
                                    <th
                                        key={column.key}
                                        className={`px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wider ${column.key === 'select' ? 'w-12' : ''}`}
                                    >
                                        {renderHeaderContent(column)}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="bg-card divide-y divide-border">
                            {isInitialLoading ? (
                                <tr>
                                    <td colSpan={visibleColumns.length} className="px-6 py-12 text-center">
                                        <div className="flex items-center justify-center gap-2 text-muted-foreground">
                                            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                            </svg>
                                            <span>{t('common.loading')}</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : filteredSubscriptions.length === 0 ? (
                                <tr>
                                    <td colSpan={visibleColumns.length} className="px-6 py-4 text-center text-sm text-muted-foreground">
                                        {filterText ? t('common.noResults') : t('common.noSubscriptions')}
                                    </td>
                                </tr>
                            ) : (
                                filteredSubscriptions.map((subscription, index) => {
                                    const isLoadMoreSentinel = hasMore && !isInitialLoading && index === loadMoreSentinelIndex

                                    return (
                                        <tr
                                            key={subscription.id}
                                            ref={isLoadMoreSentinel ? loadMoreSentinelRef : undefined}
                                            className="hover:bg-secondary/5"
                                        >
                                            {visibleColumns.map((column) => (
                                                <td key={column.key} className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                                                    {renderCellContent(subscription, column)}
                                                </td>
                                            ))}
                                        </tr>
                                    )
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {hasMore && !isInitialLoading && (
                    <div className="flex items-center justify-center py-4">
                        {isFetchingNextPage && (
                            <div className="flex items-center justify-center">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-accent"></div>
                                <span className="ms-2 text-sm text-muted-foreground">{t('common.loading')}</span>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Invitation Dialog */}
            <InvitationDialog
                isOpen={isInvitationDialogOpen}
                onClose={handleCloseDialog}
                invitation={null}
                prefilledData={prefilledInvitationData}
                onInvitationSaved={handleInvitationSaved}
                allAccessGroups={allAccessGroups}
            />

            {/* Bulk Invite Dialog */}
            <Dialog
                open={isBulkInviteDialogOpen}
                onOpenChange={(open) => !isBulkInviting && !open && handleCloseBulkInviteDialog()}
            >
                <DialogContent
                    className="sm:max-w-lg"
                    onPointerDownOutside={(e) => isBulkInviting && e.preventDefault()}
                    onEscapeKeyDown={(e) => isBulkInviting && e.preventDefault()}
                    onInteractOutside={(e) => isBulkInviting && e.preventDefault()}
                >
                    <DialogHeader>
                        <DialogTitle>
                            {t('invitations.bulkInvite')} ({selectedIds.size} {t('common.users')})
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        {/* Access Groups */}
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-foreground">
                                {t('common.accessGroups')}
                                <span className="text-error ms-1">*</span>
                            </label>
                            <MultiPick
                                options={allAccessGroups.map((g) => ({ label: g, value: g }))}
                                selected={bulkInviteFormData.accessGroups}
                                onChange={(vals) => setBulkInviteFormData(prev => ({ ...prev, accessGroups: vals }))}
                                labels={{ placeholder: t('common.pickAccessGroups') }}
                                allowNewItem
                            />
                        </div>

                        {/* Valid For */}
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-foreground">
                                {t('invitations.validFor')} ({t('common.days')})
                                <span className="text-error ms-1">*</span>
                            </label>
                            <input
                                type="number"
                                value={bulkInviteFormData.validFor}
                                onChange={(e) => setBulkInviteFormData(prev => ({
                                    ...prev,
                                    validFor: Math.max(1, parseInt(e.target.value) || 1)
                                }))}
                                disabled={isBulkInviting}
                                min="1"
                                max="365"
                                className="w-full px-3 py-2 text-sm border border-border rounded-md bg-card text-foreground placeholder-secondary/60 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-colors hover:border-secondary"
                            />
                        </div>

                        {/* Custom Body */}
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-foreground">
                                {t('invitations.customBody')}
                            </label>
                            <Textarea
                                value={bulkInviteFormData.customBody}
                                onChange={(e) => setBulkInviteFormData(prev => ({ ...prev, customBody: e.target.value }))}
                                disabled={isBulkInviting}
                                placeholder={t('common.enterValue', { value: t('invitations.customBody').toLowerCase() })}
                                className="min-h-[100px] resize-y"
                            />
                        </div>

                        {/* Inviter Selection */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-foreground">
                                    {t('invitations.inviterId')}
                                </label>
                                <UserDataInput
                                    selected={bulkInviteFormData.inviterId ? [bulkInviteFormData.inviterId] : []}
                                    onChange={(selectedIds) => {
                                        setBulkInviteFormData(prev => ({ ...prev, inviterId: selectedIds[0] || '' }))
                                    }}
                                    onChangeFullObjects={(users) => {
                                        const user = users[0]
                                        setBulkInviteFormData(prev => ({
                                            ...prev,
                                            inviterName: user ? `${user.firstName} ${user.lastName}`.trim() : '',
                                        }))
                                    }}
                                    singlePick
                                    disabled={isBulkInviting}
                                    labels={{ placeholder: `${t('common.select')} ${t('invitations.inviterId').toLowerCase()}` }}
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-foreground">
                                    {t('invitations.inviterName')}
                                </label>
                                <input
                                    type="text"
                                    value={bulkInviteFormData.inviterName}
                                    onChange={(e) => setBulkInviteFormData(prev => ({ ...prev, inviterName: e.target.value }))}
                                    disabled={isBulkInviting}
                                    placeholder={t('common.enterValue', { value: t('invitations.inviterName').toLowerCase() })}
                                    className="w-full px-3 py-2 text-sm border border-border rounded-md bg-card text-foreground placeholder-secondary/60 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-colors hover:border-secondary"
                                />
                            </div>
                        </div>

                        {/* Error Display */}
                        {bulkInviteError && (
                            <div className="p-3 bg-error/10 border border-error/20 rounded-md">
                                <p className="text-sm text-error">{bulkInviteError}</p>
                            </div>
                        )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-border justify-end">
                        <Button
                            variant="outline"
                            onClick={handleCloseBulkInviteDialog}
                            disabled={isBulkInviting}
                        >
                            {t('common.cancel')}
                        </Button>
                        <Button
                            onClick={handleBulkInvite}
                            disabled={isBulkInviting || bulkInviteFormData.accessGroups.length === 0}
                        >
                            {isBulkInviting && (
                                <svg className="animate-spin h-4 w-4 me-2" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                            )}
                            {isBulkInviting ? t('common.sending') : t('invitations.sendInvitations')}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    )
}
